import React, { useState, useContext, useEffect, Suspense } from 'react';
import { Button } from '../UI';
import TagSelector from './TagSelector';
import { PasswordStrengthAnalyzer } from '../Security';
import { PasswordGenerator, LoadingFallback } from '../LazyComponents.jsx';
import { checkPasswordDuplicate } from '../../utils/duplicateDetector';
import { PasswordContext } from '../../contexts/PasswordContext';
import { usePasswordForm } from '../../hooks/usePasswordForm';
import './PasswordForm.css';

/**
 * 새 패스워드 추가 폼 컴포넌트 (보안 기능 통합)
 * @param {Function} onSubmit - 폼 제출 핸들러
 * @param {Function} onCancel - 취소 핸들러
 * @param {boolean} loading - 로딩 상태
 * @param {object} editingPassword - 편집할 패스워드 (편집 모드)
 */
function PasswordForm({ onSubmit, onCancel, loading = false, editingPassword = null, hideHeader = false }) {
  const { passwords } = useContext(PasswordContext);
  const [showPasswordGenerator, setShowPasswordGenerator] = useState(false);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [duplicateCheck, setDuplicateCheck] = useState(null);
  const [formKey, setFormKey] = useState(Date.now()); // 폼 고유 키

  const {
    formData,
    errors,
    handleInputChange,
    handleTagsChange,
    handleSubmit,
    handleCancel,
    resetForm
  } = usePasswordForm(onSubmit, onCancel, editingPassword);

  // 새 패스워드 추가 모드일 때 폼 초기화 (마운트시)
  useEffect(() => {
    if (!editingPassword) {
      // 새 고유 키 생성으로 완전히 새로운 폼 생성
      setFormKey(Date.now());
      resetForm();
      setPasswordVisible(false);
      setDuplicateCheck(null);
      setShowPasswordGenerator(false);
      
      // 브라우저 자동완성이 적용된 후 강제로 다시 리셋 (여러 번 시도)
      const timeouts = [
        setTimeout(() => resetForm(), 50),
        setTimeout(() => resetForm(), 150),
        setTimeout(() => resetForm(), 300)
      ];
      
      return () => timeouts.forEach(clearTimeout);
    }
  }, [editingPassword, resetForm]);

  // 패스워드 변경 시 중복 검사 및 강도 분석
  const handlePasswordChange = async (e) => {
    handleInputChange(e);
    const newPassword = e.target.value;
    
    if (newPassword && newPassword.length > 0) {
      // 중복 검사 수행
      const duplicateResult = checkPasswordDuplicate(
        newPassword, 
        passwords, 
        editingPassword?.id
      );
      setDuplicateCheck(duplicateResult);
    } else {
      setDuplicateCheck(null);
    }
  };

  // 패스워드 생성기에서 패스워드 선택
  const handlePasswordGenerated = (generatedPassword) => {
    handleInputChange({
      target: {
        name: 'password',
        value: generatedPassword
      }
    });
    setShowPasswordGenerator(false);
    
    // 생성된 패스워드에 대해서도 중복 검사
    const duplicateResult = checkPasswordDuplicate(
      generatedPassword, 
      passwords, 
      editingPassword?.id
    );
    setDuplicateCheck(duplicateResult);
  };

  // 패스워드 보기/숨기기 토글
  const togglePasswordVisibility = () => {
    setPasswordVisible(!passwordVisible);
  };

  // 모달 배경 클릭으로 닫기
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      handleCancel();
    }
  };

  // 폼 제출 전 추가 검증
  const handleFormSubmit = (e) => {
    e.preventDefault();
    
    // 중복 패스워드 경고
    if (duplicateCheck?.isDuplicate) {
      const confirmSubmit = window.confirm(
        '중복된 패스워드입니다. 그래도 저장하시겠습니까?\n\n' +
        '보안을 위해 각 계정마다 고유한 패스워드 사용을 권장합니다.'
      );
      if (!confirmSubmit) {
        return;
      }
    }
    
    handleSubmit(e);
  };

  return (
    <div className="password-form-modal-content enhanced">
      {!hideHeader && (
        <div className="password-form-modal-header">
          <h3>
            {editingPassword ? '✏️ 패스워드 편집' : '➕ 새 패스워드 추가'}
          </h3>
          <button 
            className="password-form-modal-close"
            onClick={handleCancel}
            title="닫기"
            disabled={loading}
          >
            ✕
          </button>
        </div>
      )}

      <div className="password-form-modal-body">
          <form 
            onSubmit={handleFormSubmit} 
            className="password-form enhanced"
            autoComplete="off"
            noValidate
          >
            {/* 제목 필드 */}
            <div className="password-form__field-group">
              <label className="password-form__label password-form__label--required">
                제목
              </label>
              <input
                type="text"
                name="title"
                id={`title-${formKey}`}
                value={formData.title}
                onChange={handleInputChange}
                placeholder="예: Google 계정"
                className={`password-form__input ${errors.title ? 'password-form__input--error' : ''}`}
                disabled={loading}
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck="false"
                data-lpignore="true"
                data-form-type="other"
                key={`title-${formKey}`}
              />
              {errors.title && (
                <span className="password-form__error">{errors.title}</span>
              )}
            </div>

            {/* 사용자명 필드 */}
            <div className="password-form__field-group">
              <label className="password-form__label password-form__label--required">
                사용자명
              </label>
              <input
                type="text"
                name="username"
                id={`username-${formKey}`}
                value={formData.username}
                onChange={handleInputChange}
                placeholder="예: myemail@gmail.com"
                className={`password-form__input ${errors.username ? 'password-form__input--error' : ''}`}
                disabled={loading}
                autoComplete="new-username"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck="false"
                data-lpignore="true"
                data-form-type="other"
                key={`username-${formKey}`}
              />
              {errors.username && (
                <span className="password-form__error">{errors.username}</span>
              )}
            </div>

            {/* 패스워드 필드 - 향상된 보안 기능 */}
            <div className="password-form__field-group password-field-enhanced">
              <label className="password-form__label password-form__label--required">
                패스워드
              </label>
              
              <div className="password-input-container">
                <input
                  type={passwordVisible ? "text" : "password"}
                  name="password"
                  id={`password-${formKey}`}
                  value={formData.password}
                  onChange={handlePasswordChange}
                  placeholder="패스워드를 입력하세요"
                  className={`password-form__input ${errors.password ? 'password-form__input--error' : ''} ${
                    duplicateCheck?.isDuplicate ? 'password-form__input--warning' : ''
                  }`}
                  disabled={loading}
                  autoComplete="new-password"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck="false"
                  data-lpignore="true"
                  data-form-type="other"
                  key={`password-${formKey}`}
                />
                
                <div className="password-input-actions">
                  <button
                    type="button"
                    className="password-toggle-btn"
                    onClick={togglePasswordVisibility}
                    title={passwordVisible ? "패스워드 숨기기" : "패스워드 보기"}
                  >
                    {passwordVisible ? '🙈' : '👁️'}
                  </button>
                  
                  <button
                    type="button"
                    className="password-generate-btn"
                    onClick={() => setShowPasswordGenerator(true)}
                    title="안전한 패스워드 생성"
                  >
                    🎲
                  </button>
                </div>
              </div>

              {errors.password && (
                <span className="password-form__error">{errors.password}</span>
              )}

              {/* 중복 패스워드 경고 */}
              {duplicateCheck?.isDuplicate && (
                <div className="password-duplicate-warning">
                  <span className="warning-icon">⚠️</span>
                  <span className="warning-text">{duplicateCheck.recommendation}</span>
                </div>
              )}

              {duplicateCheck?.isSimilar && !duplicateCheck?.isDuplicate && (
                <div className="password-similar-warning">
                  <span className="warning-icon">💡</span>
                  <span className="warning-text">{duplicateCheck.recommendation}</span>
                </div>
              )}

              {/* 패스워드 강도 분석기 */}
              {formData.password && (
                <PasswordStrengthAnalyzer 
                  password={formData.password}
                  userInputs={[formData.title, formData.username, formData.website]}
                  showDetails={true}
                />
              )}
            </div>

            {/* 웹사이트 필드 */}
            <div className="password-form__field-group">
              <label className="password-form__label">웹사이트</label>
              <input
                type="url"
                name="website"
                id={`website-${formKey}`}
                value={formData.website}
                onChange={handleInputChange}
                placeholder="예: https://accounts.google.com"
                className="password-form__input"
                disabled={loading}
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck="false"
                data-lpignore="true"
                data-form-type="other"
                key={`website-${formKey}`}
              />
            </div>

            {/* 카테고리 필드 */}
            <div className="password-form__field-group">
              <label className="password-form__label">카테고리</label>
              <select
                name="category"
                id={`category-${formKey}`}
                value={formData.category}
                onChange={handleInputChange}
                className="password-form__select"
                disabled={loading}
                autoComplete="off"
                data-lpignore="true"
                data-form-type="other"
                key={`category-${formKey}`}
              >
                <option value="">카테고리 선택</option>
                <option value="website">웹사이트</option>
                <option value="email">이메일</option>
                <option value="entertainment">엔터테인먼트</option>
                <option value="finance">금융</option>
                <option value="keys-apis">Keys/APIs</option>
                <option value="other">기타</option>
              </select>
            </div>

            {/* 태그 필드 */}
            <div className="password-form__field-group">
              <label className="password-form__label">태그</label>
              <TagSelector
                selectedTags={formData.tags}
                onTagsChange={handleTagsChange}
                maxTags={10}
                allowAddNew={true}
              />
            </div>

            {/* 메모 필드 */}
            <div className="password-form__field-group">
              <label className="password-form__label">메모</label>
              <textarea
                name="notes"
                id={`notes-${formKey}`}
                value={formData.notes}
                onChange={handleInputChange}
                placeholder="추가 메모나 설명을 입력하세요"
                rows={3}
                className="password-form__textarea"
                disabled={loading}
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck="false"
                data-lpignore="true"
                data-form-type="other"
                key={`notes-${formKey}`}
              />
            </div>
          </form>
        </div>

        <div className="password-form-modal-footer">
          <div style={{ flex: 1 }}>
            <small style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>
              🔐 패스워드는 AES-256 암호화로 안전하게 저장됩니다.
            </small>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <Button
              variant="secondary"
              size="md"
              onClick={handleCancel}
              disabled={loading}
            >
              취소
            </Button>
            <Button
              variant="primary"
              size="md"
              onClick={handleFormSubmit}
              disabled={loading}
              loading={loading}
            >
              {loading ? '저장 중...' : editingPassword ? '수정' : '저장'}
            </Button>
          </div>
        </div>

      {/* 패스워드 생성기 모달 */}
      {showPasswordGenerator && (
        <div className="generator-modal-overlay">
          <div className="generator-modal-content">
            <Suspense fallback={<LoadingFallback message="패스워드 생성기 로딩 중..." />}>
              <PasswordGenerator 
                onPasswordGenerated={handlePasswordGenerated}
                onClose={() => setShowPasswordGenerator(false)}
              />
            </Suspense>
          </div>
        </div>
      )}
    </div>
  );
}

export default PasswordForm;